import type { AppShellTab, SideNavItem } from '@ui';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { useSession } from '../features/identity/session';

/** Path of the page with the destinations that are not in the tab bar (#80). */
export const MORE_PATH = '/mehr';

/**
 * All areas of the app the member may read (WEB-003): the full list for the
 * sidebar, the four most used as tabs for phones and tablets. The rest is on
 * the page "Mehr", whose tab is active wherever none of the four is.
 */
export function useAppNavigation() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { can } = useSession();

  const items: SideNavItem[] = [
    {
      key: 'dashboard',
      label: t('nav.dashboard'),
      href: '/',
      icon: 'home',
      active: pathname === '/',
    },
    ...(can('colonies.read')
      ? [
          {
            key: 'colonies',
            label: t('nav.colonies'),
            href: '/voelker',
            icon: 'hive' as const,
            active: pathname.startsWith('/voelker'),
            section: t('nav.sections.apiary'),
          },
        ]
      : []),
    ...(can('locations.read')
      ? [
          {
            key: 'locations',
            label: t('nav.locations'),
            href: '/standorte',
            icon: 'map-pin' as const,
            active: pathname.startsWith('/standorte'),
            section: t('nav.sections.apiary'),
          },
        ]
      : []),
    ...(can('harvest.read')
      ? [
          {
            key: 'harvest',
            label: t('nav.harvest'),
            href: '/ernte',
            icon: 'honeycomb' as const,
            active: pathname.startsWith('/ernte'),
            section: t('nav.sections.harvest'),
          },
        ]
      : []),
    ...(can('stock.read')
      ? [
          {
            key: 'stock',
            label: t('nav.stock'),
            href: '/bestand',
            icon: 'jar' as const,
            active: pathname.startsWith('/bestand'),
            section: t('nav.sections.harvest'),
          },
        ]
      : []),
    ...(can('customers.read')
      ? [
          {
            key: 'customers',
            label: t('nav.customers'),
            href: '/kunden',
            icon: 'users' as const,
            active: pathname.startsWith('/kunden'),
            section: t('nav.sections.sales'),
          },
        ]
      : []),
    ...(can('sales.read')
      ? [
          {
            key: 'sales',
            label: t('nav.sales'),
            href: '/verkauf',
            icon: 'shopping-cart' as const,
            active: pathname.startsWith('/verkauf'),
            section: t('nav.sections.sales'),
          },
        ]
      : []),
    ...(can('tasks.read')
      ? [
          {
            key: 'tasks',
            label: t('nav.tasks'),
            href: '/aufgaben',
            icon: 'list-checks' as const,
            active: pathname.startsWith('/aufgaben'),
            section: t('nav.sections.organization'),
          },
        ]
      : []),
    ...(can('finance.read')
      ? [
          {
            key: 'finance',
            label: t('nav.finance'),
            href: '/finanzen',
            icon: 'wallet' as const,
            active: pathname.startsWith('/finanzen'),
            section: t('nav.sections.organization'),
          },
        ]
      : []),
    ...(can('reports.read')
      ? [
          {
            key: 'reports',
            label: t('nav.reports'),
            href: '/auswertungen',
            icon: 'bar-chart' as const,
            active: pathname.startsWith('/auswertungen'),
            section: t('nav.sections.organization'),
          },
        ]
      : []),
    {
      key: 'settings',
      label: t('nav.settings'),
      href: '/einstellungen',
      icon: 'settings',
      active: pathname.startsWith('/einstellungen'),
      section: t('nav.sections.workspace'),
    },
  ];

  // Tab bar on phones and tablets: the four most used areas, the rest under "Mehr".
  const tabOrder = ['dashboard', 'colonies', 'sales', 'tasks', 'harvest', 'customers'];
  const tabs: AppShellTab[] = tabOrder
    .map((key) => items.find((i) => i.key === key))
    .filter((i): i is SideNavItem & { icon: NonNullable<SideNavItem['icon']> } => !!i?.icon)
    .slice(0, 4)
    .map(({ key, label, href, icon, active }) => ({
      key,
      label,
      href,
      icon,
      ...(active ? { active } : {}),
    }));

  const rest = items.filter((item) => !tabs.some((tab) => tab.key === item.key));
  const moreActive = !tabs.some((tab) => tab.active);
  return { items, tabs, rest, moreActive };
}
